<template>
  <v-card class="py-2 mx-auto" :max-width="MAX_CONTENT_WIDTH">
    <h1 class="page-title"> Explore {{ userFriendlyMediumWord(this.$route.params.medium, true) }} </h1>
    <v-divider></v-divider>
    <div class="d-flex justify-end">
      <div class="filter-wrapper"
        ><select-dense
          v-model="sortBy"
          :items="sortOptions"
          @change="handleFilterChanged"
          item-text="userFriendly"
          item-value="value"
          :clearable="false"
        ></select-dense>
      </div>
    </div>
    <v-divider></v-divider>
    <v-list class="py-0" id="work-list">
      <template v-for="item in works">
        <v-list-item :key="works.indexOf(item)" class="py-2 pl-5 d-flex align-start">
          <v-img
            :src="item.posterSource"
            class="reviewPoster rounded"
            :height="posterWidth / getAspectRatio(item.medium)"
            :width="posterWidth"
            :max-width="posterWidth"
            @click="routeToWorkPage(item.medium, item.id)"
          ></v-img>
          <div class="work-info">
            <p @click="routeToWorkPage(item.medium, item.id)" class="work-title">
              <strong> {{ item.title }}</strong>
            </p>
            <p class="details">
              <!-- TODO: Use a helper function similar to the saved-for-later `subtext` function-->
              <span v-if="item.creators">{{ item.creators.map((creator) => creator.name).join(",") }}</span>
              <span v-if="item.creators && item.creators.length > 0 && item.releaseDate"> • </span>
              <span v-if="item.releaseDate">{{ formatDate(item.releaseDate) }}</span>
            </p>
            <p class="rating" v-if="item.rating">
              {{ item.rating % 1 === 0 ? parseInt(item.rating) : item.rating.toFixed(1) }}/10
            </p>
            <p class="audience-size">
              <v-icon size="16" class="audience-size-icon">mdi-account-group</v-icon>
              {{ item.audienceSize }}
            </p>
          </div>
        </v-list-item>
        <v-divider :key="item.id + 'divider'" />
      </template>
    </v-list>
  </v-card>
</template>

<script>
// Components
import SelectDense from "../components/select-dense.vue";
// Mixins
import getTopWorks from "../mixins/getTopWorks";
import SessionTools from "../mixins/sessionTools";
// Util
import { MAX_CONTENT_WIDTH } from "../util/constants";
import { userFriendlyMediumWord } from "../util/mediumHelpers";
import getAspectRatio from "../util/getAspectRatio";
import { formatDate } from "../util/dateHelpers";

export default {
  name: "explore-page",
  components: { SelectDense },
  mixins: [getTopWorks, SessionTools],

  data: () => ({
    MAX_CONTENT_WIDTH,
    ITEMS_PER_LOAD: 100,

    works: [],
    hasMoreResults: true,
    loadingMoreResults: false,
    headers: [
      { text: "Title", value: "title" },
      { text: "Telemonial Average", value: "average" },
    ],
    sortBy: "audienceSize",
    sortOptions: [
      {
        userFriendly: "Audience Size",
        value: "audienceSize",
      },
      {
        userFriendly: "Rating",
        value: "rating",
      },
    ],
  }),

  computed: {
    aspect() {
      let medium = this.$route.params.medium;
      return [this.$mediums.ALBUM, this.$mediums.PODCAST].includes(medium) ? 1 : 2 / 3;
    },
    posterWidth() {
      if (this.$vuetify.breakpoint.mdAndUp) {
        return 100;
      } else {
        return 64;
      }
    },
  },

  watch: {
    $route() {
      this.resetWorks();
    },
  },

  mounted: function () {
    this.loadMoreWorks();
    window.addEventListener("scroll", this.handleScroll);
  },

  destroyed: function () {
    window.removeEventListener("scroll", this.handleScroll);
  },

  methods: {
    userFriendlyMediumWord,
    getAspectRatio,
    formatDate,

    loadMoreWorks: async function () {
      if (this.hasMoreResults && !this.loadingMoreResults) {
        this.loadingMoreResults = true;
        const newWorks = await this.getTopWorksV1(
          this.$route.params.medium,
          this.ITEMS_PER_LOAD,
          this.sortBy,
          this.works.length,
        );
        this.works = this.works.concat(newWorks);
        if (newWorks.length < this.ITEMS_PER_LOAD) {
          this.hasMoreResults = false;
        }
        this.loadingMoreResults = false;
      }
    },

    resetWorks: async function () {
      this.works = [];
      this.hasMoreResults = true;
      this.loadMoreWorks();
    },

    routeToWorkPage: async function (medium, id) {
      await this.$router.push({
        path: "/work/" + medium + "/" + id,
      });
    },

    routeToRatePage: async function (medium, id) {
      await this.$router.push({
        path: "/rate/",
        query: {
          medium,
          workID: id,
        },
      });
    },

    handleFilterChanged: async function () {
      this.resetWorks();
    },

    handleScroll: async function () {
      if (this.loading) return;
      let bottom = document.getElementById("work-list").getBoundingClientRect().bottom;

      if (bottom - 500 < window.innerHeight && !this.endOfReviews) {
        this.loadMoreWorks();
      }
    },
  },
};
</script>

<style scoped>
.filter-wrapper {
  width: 180px;
}

.reviewPoster {
  cursor: pointer;
  justify-content: center;
  margin-right: 6px;
}

.work-info {
  height: 100%;
  text-align: start;
}

.work-info p {
  margin-bottom: 0;
}

.work-title {
  line-height: 1;
}

.work-title:hover {
  cursor: pointer;
  text-decoration: underline;
}

.work-info .details {
  font-size: 12px;
  color: var(--text-middle-lightness);
}

.rating {
  font-weight: bold;
  font-size: 16px;
  line-height: 1;
}

.audience-size {
  color: var(--text-middle-lightness);
  font-size: 14px;
  line-height: 1;
}
.audience-size .audience-size-icon {
  transform: translate(0, -2px);
}

@media (min-width: 960px) {
  .work-info p {
    margin-bottom: 1px;
  }
  .work-title {
    font-size: 18px;
  }
  .rating {
    font-size: 18px;
  }
  .work-info .details {
    font-size: 16px;
  }
  .audience-size {
    font-size: 16px;
  }
}
</style>
